import React, { useEffect, useState, type ReactNode } from "react";
import type { PaginatorPageChangeEvent } from "primereact/paginator";
import ExpensesListViewModel from "../../viewmodels/expenses/ExpensesListViewModel";
import ExpenseFilters from "../../components/expenses/ExpenseFilters";
import {
  createExpenseColumns,
  ExpensesMobileCards,
} from "../../components/expenses/ExpenseListPresentation";
import UploadExpenseForm from "../../components/expenses/UploadExpenseForm";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import EmptyModal from "../../components/modals/EmptyModal";
import ProgressModal from "../../components/modals/ProgressModal";
import { useToast } from "../../contexts/ToastContext";
import { getExpenseReceiptFile } from "../../shared/expenseReceiptApi";
import { getExpenseUserOptions } from "../../shared/expenseUsersApi";
import type { ExpenseUserOption } from "../../shared/expenseUsersApi";
import type { ExpenseListItem } from "../../components/expenses/ExpenseListPresentation";
import type { ExpenseMutationPayload, ExpenseRecord } from "../../types/expenses";

interface ModalState {
  open: boolean;
  childComponent: ReactNode;
  dismissible?: boolean;
}

interface MobileCardsProps {
  data: ExpenseRecord[];
}

const closedModal: ModalState = { open: false, childComponent: null };

export default function ExpensesListView() {
  const { showSuccess } = useToast();
  const {
    loadExpenses,
    getExpense,
    expenses,
    loading,
    updateExpense,
    manualCreate,
    filters,
    setFilters,
    appliedFilters,
    applyFilters,
    resetFilters,
    deleteExpense,
  } = ExpensesListViewModel();

  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [expenseUsers, setExpenseUsers] = useState<ExpenseUserOption[]>([]);
  const [expenseUsersLoading, setExpenseUsersLoading] = useState(true);
  const [emptyModal, setEmptyModal] = useState<ModalState>(closedModal);
  const [progressModal, setProgressModal] = useState<ModalState>(closedModal);

  const closeExpenseModal = () => setEmptyModal(closedModal);
  const refreshExpenses = () => loadExpenses(currentPage, searchTerm, appliedFilters);

  useEffect(() => {
    let active = true;
    getExpenseUserOptions()
      .then((options) => {
        if (active) setExpenseUsers(options);
      })
      .catch((error) => {
        console.error("Could not load expense users", error);
      })
      .finally(() => {
        if (active) setExpenseUsersLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // Editing the filter dropdown does not refresh the list until Apply is used.
  useEffect(() => {
    const timer = setTimeout(refreshExpenses, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, appliedFilters]);

  const handleSearch = (term: string) => {
    setCurrentPage(1);
    setFirst(0);
    setSearchTerm(term);
  };

  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    setCurrentPage(event.page + 1);
    setFirst(event.first);
    setRows(event.rows);
  };

  const openExpense = async (expense: ExpenseListItem) => {
    const result = await getExpense(expense.id);
    if (!result.success) {
      console.error("Could not load expense details", result.data);
      return;
    }

    const details = result.data;
    setEmptyModal({
      open: true,
      childComponent: (
        <UploadExpenseForm
          visible
          initialData={{
            ...details,
            receipt_path: expense.receipt_path || details.receipt_path,
          }}
          onClose={closeExpenseModal}
          loadReceiptFile={getExpenseReceiptFile}
          userOptions={expenseUsers}
          userOptionsLoading={expenseUsersLoading}
          onUpdate={async (id, payload) => {
            const response = await updateExpense(id, payload);
            if (response?.success) {
              showSuccess("Expense updated successfully");
              closeExpenseModal();
              refreshExpenses();
            }
            return response;
          }}
        />
      ),
    });
  };

  const requestDelete = (expense: ExpenseListItem) => {
    setEmptyModal({
      open: true,
      childComponent: (
        <DeleteConfirmationForm
          title="Delete expense"
          message="Are you sure you want to delete this expense? This action cannot be undone."
          buttonMessage="Delete"
          setOpen={setEmptyModal}
          confirmAction={async () => {
            const response = await deleteExpense(expense.id);
            if (response?.success) {
              showSuccess(response.data.message || "Action completed");
              closeExpenseModal();
              refreshExpenses();
            } else {
              console.error("Operation failed:", response?.data?.message);
            }
            return response;
          }}
        />
      ),
    });
  };

  async function handleManualCreate(payloadOrFormData: ExpenseMutationPayload | FormData) {
    setProgressModal({
      open: true,
      childComponent: <ProgressModal message="Creating expense..." />,
    });
    try {
      const response = await manualCreate(payloadOrFormData);
      if (response?.success) {
        showSuccess("Expense created successfully");
        closeExpenseModal();
        refreshExpenses();
      } else {
        console.error("Operation failed:", response.success ? undefined : response.data.message);
      }
      return response;
    } finally {
      setProgressModal(closedModal);
    }
  }

  const headerButtons = [
    {
      label: "Manual Upload",
      onClick: () =>
        setEmptyModal({
          open: true,
          childComponent: (
            <UploadExpenseForm
              visible
              onClose={closeExpenseModal}
              onManualCreate={handleManualCreate}
              userOptions={expenseUsers}
              userOptionsLoading={expenseUsersLoading}
            />
          ),
        }),
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
    {
      label: "Automatic Upload",
      onClick: async () => {
        const AutomaticUploadModal = (
          await import("../../components/expenses/AutomaticUploadModal")
        ).default;
        setEmptyModal({
          open: true,
          dismissible: false,
          childComponent: (
            <AutomaticUploadModal
              onCancel={closeExpenseModal}
              onComplete={() => {
                showSuccess("Expense created successfully");
                closeExpenseModal();
                refreshExpenses();
              }}
              userOptions={expenseUsers}
              userOptionsLoading={expenseUsersLoading}
            />
          ),
        });
      },
      className:
        "bg-white border border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 focus:ring-primary-500",
    },
  ];

  const filterConfig = {
    dropdown: true,
    components: [
      <ExpenseFilters
        key="expense-filters"
        filters={filters}
        isAdmin={Number(localStorage.getItem("userType")) === 1}
        userOptions={expenseUsers}
        userOptionsLoading={expenseUsersLoading}
        onChange={setFilters}
      />,
    ],
    onReset: () => {
      setCurrentPage(1);
      setFirst(0);
      resetFilters();
    },
    onApply: () => {
      setCurrentPage(1);
      setFirst(0);
      applyFilters();
    },
  };

  const columns = createExpenseColumns({
    onView: openExpense,
    onDelete: requestDelete,
  });
  const MobileCards = ({ data }: MobileCardsProps) => (
    <ExpensesMobileCards data={data} onView={openExpense} onDelete={requestDelete} />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Expenses"
        description="Manage your expense records"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        columns={columns}
        data={expenses.data || []}
        loading={loading}
        pagination={{
          first,
          rows,
          total: expenses.total || 0,
          template:
            "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} expenses",
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={openExpense}
        searchPlaceholder="Search by vendor or notes"
        MobileCardsComponent={MobileCards}
        emptyMessage="No expenses found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        dismissible={emptyModal.dismissible}
        setOpen={setEmptyModal}
      />
      <EmptyModal
        childComponent={progressModal.childComponent}
        open={progressModal.open}
        setOpen={setProgressModal}
      />
    </div>
  );
}
